/**
 * Settings — Sprint AR.
 *
 * Self-service credentials surface: operators paste their API keys and
 * bot tokens here instead of editing config_encrypted via psql. Each
 * available connector renders its own form built dynamically from the
 * manifest's `required_env`. Secret fields use masked inputs; the
 * server never echoes them back (we only learn the field is "filled"
 * via the `configured_field_names` list on the installation DTO).
 *
 * Why the absent-key-keeps-existing-value pattern:
 *   - Lets operators rotate one secret at a time without re-typing
 *     all the others.
 *   - Empty submission of an already-filled field is a no-op rather
 *     than a destructive clear — destructive clears need the explicit
 *     "Удалить установку" button.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2, ExternalLink, KeyRound, Plug, Trash2, Zap,
} from "lucide-react";
import { Badge, Button, Card, CardHeader, Input, cn } from "@vseh/ui";

import {
  connectorsApi,
  type AvailableConnectorDto,
  type ConnectorTestResult,
  type InstallationDto,
  type RequiredEnvDto,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import {
  EmptyBlock, ErrorBlock, LoadingBlock,
} from "../shared/components/States";


export function SettingsPage(): JSX.Element {
  const t = useT();
  const available = useFetch<AvailableConnectorDto[]>(
    (signal) => connectorsApi.listAvailable({ signal }), [],
  );
  const installed = useFetch<InstallationDto[]>(
    (signal) => connectorsApi.listInstalled({ signal }), [],
  );

  const refreshAll = useCallback(() => {
    available.refetch();
    installed.refetch();
  }, [available, installed]);

  // Index installs by connector_name for O(1) lookups inside the
  // available-connectors render loop.
  const installedByName = useMemo(() => {
    const map = new Map<string, InstallationDto>();
    for (const i of installed.data ?? []) map.set(i.connector_name, i);
    return map;
  }, [installed.data]);

  const loading = (available.loading && !available.data)
                  || (installed.loading && !installed.data);
  const error = available.error ?? installed.error;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={t("settings.title")}
          description={
            <>
              {t("settings.desc.line1")}{" "}
              {t("settings.desc.line2.pre")}<b>{t("settings.desc.line2.bold")}</b>{t("settings.desc.line2.post")}
            </>
          }
          actions={
            <Button variant="ghost" size="xs" onClick={refreshAll}>
              {t("common.refresh")}
            </Button>
          }
        />

        {loading && <LoadingBlock />}
        {error && <ErrorBlock error={error} onRetry={refreshAll} />}

        {available.data && available.data.length === 0 && (
          <EmptyBlock
            title={t("settings.empty.title")}
            description={t("settings.empty.description")}
          />
        )}

        {available.data && available.data.length > 0 && (
          <ul className="space-y-3">
            {available.data.map((c) => (
              <li key={c.name}>
                <ConnectorCredentialsForm
                  connector={c}
                  installation={installedByName.get(c.name) ?? null}
                  onChanged={refreshAll}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}


function ConnectorCredentialsForm(props: {
  connector:    AvailableConnectorDto;
  installation: InstallationDto | null;
  onChanged:    () => void;
}): JSX.Element {
  const t = useT();
  const { connector, installation, onChanged } = props;
  // Form state — keyed by field name. Always strings (we ship as JSON
  // via the install endpoint; coercion happens server-side per
  // connector if a field is numeric).
  const [values,    setValues]    = useState<Record<string, string>>({});
  const [enabled,   setEnabled]   = useState<boolean>(installation?.enabled ?? true);
  const [busy,      setBusy]      = useState(false);
  const [feedback,  setFeedback]  = useState<string | null>(null);
  const [error,     setError]     = useState<Error | null>(null);
  // Sprint M-test — last "Test connection" verdict. `null` until
  // the operator clicks the button; cleared by every save/uninstall.
  const [testing,   setTesting]   = useState(false);
  const [testResult, setTestResult] = useState<ConnectorTestResult | null>(null);

  // Reset when the user switches to a different connector's row.
  useEffect(() => {
    setValues({});
    setEnabled(installation?.enabled ?? true);
    setFeedback(null);
    setError(null);
    setTestResult(null);
  }, [connector.name, installation?.id]);

  const isConfigured = (fieldName: string): boolean =>
    (installation?.configured_field_names ?? []).includes(fieldName);

  const onSave = useCallback(async () => {
    setBusy(true);
    setError(null);
    setFeedback(null);
    setTestResult(null);                              // stale after save
    try {
      await connectorsApi.install({
        connector_name: connector.name,
        config:         values,
        enabled,
      });
      setValues({});
      setFeedback(t("settings.feedback.saved"));
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }, [connector.name, values, enabled, onChanged]);

  // Sprint M-test — verify whatever is currently stored against the
  // upstream API. Important: this runs against the SAVED config, not
  // the form draft. The operator needs to Save first, then Test —
  // we make that explicit by graying the button when there are
  // unsaved changes in `values`.
  const onTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await connectorsApi.testConnection(connector.name);
      setTestResult(r);
    } catch (e) {
      setTestResult({
        ok: false,
        message: e instanceof Error ? e.message : String(e),
        detail: {},
      });
    } finally {
      setTesting(false);
    }
  }, [connector.name]);

  const onUninstall = useCallback(async () => {
    // Confirmation lives in browser-native to avoid pulling a modal
    // dep just for this; the action is rare and reversible (operator
    // re-enters credentials).
    if (!window.confirm(
      t("settings.confirm.uninstall", { name: connector.name }),
    )) return;
    setBusy(true);
    setError(null);
    setFeedback(null);
    setTestResult(null);
    try {
      await connectorsApi.uninstall(connector.name);
      setFeedback(t("settings.feedback.removed"));
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }, [connector.name, onChanged]);

  const hasUnsavedChanges = Object.keys(values).length > 0;
  const canTest = installation !== null && !hasUnsavedChanges && !busy;

  const hasFields = connector.required_env.length > 0;

  return (
    <div className="rounded-md border border-base-200 bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Plug className="size-4 text-base-500" aria-hidden="true" />
            <span className="font-mono text-sm font-medium text-base-900">
              {connector.name}
            </span>
            <Badge tone="neutral">v{connector.version}</Badge>
            {installation ? (
              installation.enabled ? (
                <Badge tone="success" dot>{t("settings.badge.installed")}</Badge>
              ) : (
                <Badge tone="neutral" dot>{t("settings.badge.disabled")}</Badge>
              )
            ) : (
              <Badge tone="neutral">{t("settings.badge.not_installed")}</Badge>
            )}
            {connector.homepage && (
              <a
                href={connector.homepage}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-2xs text-ai-700 hover:underline"
              >
                docs <ExternalLink className="size-3" aria-hidden="true" />
              </a>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-base-600">
            {connector.description}
          </p>
        </div>
      </div>

      {hasFields ? (
        <div className="mt-3 space-y-2">
          {connector.required_env.map((field) => (
            <CredentialField
              key={field.name}
              field={field}
              value={values[field.name] ?? ""}
              configured={isConfigured(field.name)}
              onChange={(v) =>
                setValues((prev) => ({ ...prev, [field.name]: v }))
              }
            />
          ))}
        </div>
      ) : (
        <p className="mt-3 text-2xs text-base-500">
          {t("settings.no_creds")}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-base-100 pt-3">
        <label className="flex items-center gap-1.5 text-xs text-base-700">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="size-3.5 rounded-sm border-base-300"
          />
          {t("settings.enabled")}
        </label>
        <div className="flex-1" />
        {installation && (
          <Button
            variant="ghost"
            size="xs"
            leftIcon={<Trash2 className="size-3.5" />}
            onClick={onUninstall}
            disabled={busy}
          >
            {t("settings.uninstall")}
          </Button>
        )}
        {/* Sprint M-test — only render when there's something to test.
            Gray + disabled when the operator has unsaved changes (we
            test the saved blob, not the form draft). */}
        {installation && (
          <Button
            variant="ghost"
            size="xs"
            leftIcon={<Zap className="size-3.5" />}
            onClick={onTest}
            disabled={!canTest}
            loading={testing}
          >
            {t("settings.test_connection")}
          </Button>
        )}
        <Button
          variant="ai"
          size="sm"
          leftIcon={<KeyRound className="size-3.5" />}
          loading={busy}
          onClick={onSave}
        >
          {installation ? t("settings.save_changes") : t("settings.install")}
        </Button>
      </div>

      {feedback && (
        <div className="mt-2 rounded-sm border border-success-200 bg-success-50 px-3 py-1.5 text-2xs text-success-700">
          {feedback}
        </div>
      )}
      {error && (
        <div className="mt-2 rounded-sm border border-danger-200 bg-danger-50 px-3 py-1.5 text-2xs text-danger-700">
          {error.message}
        </div>
      )}
      {testResult && (
        <div
          className={cn(
            "mt-2 rounded-sm border px-3 py-1.5 text-2xs",
            testResult.ok
              ? "border-success-200 bg-success-50 text-success-700"
              : "border-danger-200 bg-danger-50 text-danger-700",
          )}
        >
          <div className="font-medium">
            {testResult.ok
              ? t("settings.test.ok")
              : t("settings.test.fail")}
          </div>
          <div className="mt-0.5 opacity-90">{testResult.message}</div>
          {Object.keys(testResult.detail).length > 0 && (
            <pre className="mt-1 overflow-x-auto rounded-sm bg-white/40 px-1.5 py-1 font-mono text-3xs">
              {JSON.stringify(testResult.detail, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}


function CredentialField(props: {
  field:       RequiredEnvDto;
  value:       string;
  configured:  boolean;
  onChange:    (v: string) => void;
}): JSX.Element {
  const t = useT();
  const { field, value, configured, onChange } = props;
  return (
    <div>
      <div className="flex items-center justify-between">
        <label
          htmlFor={`cred-${field.name}`}
          className="text-xs font-medium text-base-800"
        >
          <span className="font-mono">{field.name}</span>
          {field.secret && (
            <span className="ml-2 text-2xs font-normal text-base-500">
              {t("settings.field.secret")}
            </span>
          )}
        </label>
        {configured && (
          <span className="flex items-center gap-1 text-2xs text-success-700">
            <CheckCircle2 className="size-3" aria-hidden="true" />
            {t("settings.field.filled")}
          </span>
        )}
      </div>
      <Input
        id={`cred-${field.name}`}
        type={field.secret ? "password" : "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={configured ? t("settings.field.placeholder.configured") : field.description ?? ""}
        autoComplete="off"
        spellCheck={false}
      />
      {field.description && (
        <div
          className={cn(
            "mt-1 text-2xs text-base-500",
            configured && "opacity-60",
          )}
        >
          {field.description}
        </div>
      )}
    </div>
  );
}
